import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights } from "@MasterBluePrint/Styles/variables";
import React, { ReactNode, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    pathName = "pathName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.pathName]: string;
    }
}

const mapStateToProps = (state: ApplicationState) => ({
    location: getLocation(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface OrderStatusFormStyles {
    form?: InjectableCss;
    orderNumberTextField?: TextFieldPresentationProps;
    emailOrPostalCodeTextField?: TextFieldPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const orderStatusFormStyles: OrderStatusFormStyles = {
    form: {
        css: css`
            display: flex;

            @media print {
                display: none;
            }

            label {
                font-weight: ${themeFontWeights.fontWeightMedium};
                color: ${themeColors.colorPrimary} !important;
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size14} !important;
                letter-spacing: 0.01rem;
                line-height: 1.4375rem;

                span {
                    color: ${themeColors.colorMidGrey} !important;
                    font-family: 'Roboto', sans-serif;
                    font-size: ${themeFontSizes.size12} !important;
                    font-style: italic;
                    letter-spacing: 0.0081rem;
                    line-height: 1.4375rem;
                }
                
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${themeFontSizes.size13} !important;
                        `,
                    ],
                    "minMax",
                )}
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                        flex-direction: column;
                    `,
                    ],
                    "max",
                )}
        `,
    },
    orderNumberTextField: {
        cssOverrides: {
            formField: css`
                width: auto;
                margin-right: 1.875rem;
                
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                            margin-bottom: 0.9375rem;
                        `,
                        ],
                        "max",
                    )}    
            `,
        },
    },
    emailOrPostalCodeTextField: {
        cssOverrides: {
            formField: css`
                width: auto;
                margin-right: 30px;
            `,
        },
    },
    submitButton: {
        css: css`
                margin-top: 2rem;
                border: 1px solid ${themeColors.colorPrimary};
                border-radius: 2px;
                background: ${themeColors.colorWhite};
                width: 179px;
                color: ${themeColors.colorPrimary};
                font-family: Roboto;
                font-size: ${themeFontSizes.size14};
                font-weight: ${themeFontWeights.fontWeightForBold};
                letter-spacing: 0;
                line-height: 1.3125rem;
                text-align: center;
                text-transform: uppercase;
            &:hover {
                color: ${themeColors.colorWhite};
                background: ${themeColors.colorPrimary};
                border: 1px solid ${themeColors.colorPrimary};
            }
            @media screen and (min-width: 1024px) and (max-width: 1200px) {
                margin-top: 3.4375rem;
            }       
        `,
    },
};

const styles = orderStatusFormStyles;
const StyledForm = getStyledWrapper("form");

const OrderStatusForm = ({ fields, location, history }: Props) => {
    const [orderNumber, setOrderNumber] = useState("");
    const [orderNumberError, setOrderNumberError] = useState<ReactNode>("");
    const [emailOrPostalCode, setEmailOrPostalCode] = useState("");
    const [emailOrPostalCodeError, setEmailOrPostalCodeError] = useState<ReactNode>("");

    const orderNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOrderNumber(event.target.value);
        setOrderNumberError(!event.target.value.trim() ? siteMessage("OrderStatusLookup_OrderNumberRequired") : "");
    };

    const emailOrPostalCodeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailOrPostalCode(event.target.value);
        setEmailOrPostalCodeError(
            !event.target.value.trim() ? siteMessage("OrderStatusLookup_EmailOrPostalCodeRequired") : "",
        );
    };

    const handleSubmit = (event: React.MouseEvent<HTMLFormElement, MouseEvent>) => {
        event.preventDefault();
        const paramName = emailOrPostalCode.indexOf("@") >= 0 ? "stEmail" : "stPostalCode";
        window.location.href = `${fields.pathName}?orderNumber=${orderNumber}&${paramName}=${emailOrPostalCode}`;
    };

    return (
        <>
            <StyledForm {...styles.form} onSubmit={handleSubmit} className="order-status-form">
                <TextField
                    label={translate("Order Number")}
                    onChange={orderNumberChangeHandler}
                    error={orderNumberError}
                    required
                    {...styles.orderNumberTextField}
                    data-test-selector="orderStatusForm_orderNumber"
                />
                <TextField
                    label={translate("Ship To Postal Code")}
                    onChange={emailOrPostalCodeChangeHandler}
                    error={emailOrPostalCodeError}
                    required
                    {...styles.emailOrPostalCodeTextField}
                    data-test-selector="orderStatusForm_emailOrPostalCode"
                />
                <Button
                    {...styles.submitButton}
                    type="submit"
                    disabled={!orderNumber || !emailOrPostalCode}
                    data-test-selector="orderStatusForm_submit"
                >
                    {translate("Submit")}
                </Button>
            </StyledForm>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(OrderStatusForm)),
    definition: {
        group: "Order Status",
        displayName: "Form",
        allowedContexts: ["OrderStatusPage"],
        fieldDefinitions: [{
            name: fields.pathName,
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
        }]
    },
};

export default widgetModule;